import React, { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import api from "../utils/api";
import io from "socket.io-client";

import {
  GoogleMap,
  Marker,
  Polyline,
  useLoadScript,
} from "@react-google-maps/api";

/* ================= SOCKET ================= */

const socket = io(
  "https://api.freshcart4u.com",
  {
    transports: ["websocket"],
    reconnection: true,
    reconnectionAttempts: 10,
    reconnectionDelay: 1000,
  }
);

/* ================= MAP ================= */

const containerStyle = {
  width: "100%",
  height: "500px",
  borderRadius: "16px",
};

const OrderTracking = () => {

  const { orderId } = useParams();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey:
      process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const [order, setOrder] = useState(null);

  const [riderLocation, setRiderLocation] =
    useState(null);

  const [smoothLocation, setSmoothLocation] =
    useState(null);

  const intervalRef = useRef(null);

  /* ================= FETCH ORDER ================= */

  const fetchOrder = async () => {

    try {

      const res = await api.get(
        `/api/orders/${orderId}`
      );

      const orderData = res.data;

      setOrder(orderData);

      const liveLocation =
        orderData?.delivery?.deliveryBoy?.location;

      if (
        liveLocation?.lat &&
        liveLocation?.lng
      ) {

        setRiderLocation({
          lat: Number(liveLocation.lat),
          lng: Number(liveLocation.lng),
        });

        if (!smoothLocation) {
          setSmoothLocation({
            lat: Number(liveLocation.lat),
            lng: Number(liveLocation.lng),
          });
        }

      }

    } catch (err) {

      console.error(
        "Fetch Order Error:",
        err
      );

    }

  };

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {

    fetchOrder();

    const interval = setInterval(() => {

      fetchOrder();

    }, 5000);

    return () => clearInterval(interval);

  }, [orderId]);
  /* ================= SOCKET ================= */

  useEffect(() => {

    socket.connect();

    socket.emit("joinOrderRoom", orderId);

    socket.on("connect", () => {
      console.log("✅ Socket Connected");
    });

    socket.on("disconnect", () => {
      console.log("❌ Socket Disconnected");
    });

    /* ===== LIVE LOCATION ===== */

    socket.on("deliveryBoyLiveLocation", (data) => {

      if (data.orderId !== orderId) return;

      const newLocation = {
        lat: Number(data.lat),
        lng: Number(data.lng),
      };

      setRiderLocation(newLocation);

    });

    /* ===== ETA UPDATE ===== */

    socket.on("etaUpdate", (data) => {

      if (data.orderId !== orderId) return;

      setOrder((prev) => {

        if (!prev) return prev;

        return {
          ...prev,
          etaText: data.etaText,
          distance: data.distance,
        };

      });

    });

    /* ===== STATUS UPDATE ===== */

    socket.on("orderStatusUpdated", (data) => {

      if (data.orderId !== orderId) return;

      setOrder((prev) => {

        if (!prev) return prev;

        return {
          ...prev,
          orderStatus: data.status,
        };

      });

    });

    return () => {

      socket.off("deliveryBoyLiveLocation");

      socket.off("etaUpdate");

      socket.off("orderStatusUpdated");

      socket.disconnect();

    };

  }, [orderId]);

  /* ================= SMOOTH ANIMATION ================= */

  useEffect(() => {

    if (!riderLocation) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    intervalRef.current = setInterval(() => {

      setSmoothLocation((prev) => {

        if (!prev) return riderLocation;

        return {

          lat:
            prev.lat +
            (riderLocation.lat - prev.lat) * 0.15,

          lng:
            prev.lng +
            (riderLocation.lng - prev.lng) * 0.15,

        };

      });

    }, 120);

    return () => {

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

    };

  }, [riderLocation]);
  /* ================= LOADING ================= */

  if (!isLoaded) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "50px",
        }}
      >
        <h3>Loading Google Maps...</h3>
      </div>
    );
  }

  if (!order) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "50px",
        }}
      >
        <h3>Loading Order...</h3>
      </div>
    );
  }

  /* ================= CUSTOMER LOCATION ================= */

  const customerLocation = {
    lat:
      Number(
        order?.shippingAddress?.lat
      ) || 0,

    lng:
      Number(
        order?.shippingAddress?.lng
      ) || 0,
  };

  /* ================= RIDER LOCATION ================= */

  const rider =
    smoothLocation || riderLocation;

  /* ================= DELIVERED ================= */

  if (
    order.orderStatus ===
    "DELIVERED"
  ) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "60px",
        }}
      >
        <h2>
          ✅ Order Delivered
        </h2>

        <p>
          Thank you for shopping
          with FreshCart 🎉
        </p>
      </div>
    );
  }

  /* ================= UI ================= */

  return (
    <div className="container py-4">

      <div className="tracking-header">

        <h3>
          📍 Live Order Tracking
        </h3>

        <div className="eta-box">
          🚴 ETA :
          <strong>
            {" "}
            {order?.etaText ||
              order
                ?.shippingAddress
                ?.eta ||
              "Calculating..."}
          </strong>
        </div>

      </div>

      {/* ================= MAP ================= */}

      <GoogleMap
        mapContainerStyle={
          containerStyle
        }
        zoom={15}
        center={
          rider ||
          customerLocation
        }
      >

        {/* CUSTOMER */}

        <Marker
          position={
            customerLocation
          }
          label="🏠"
        />

        {/* DELIVERY BOY */}

        {rider && (
          <Marker
            position={rider}
            icon={{
              url: "https://cdn-icons-png.flaticon.com/512/2972/2972185.png",
              scaledSize:
                new window.google.maps.Size(
                  42,
                  42
                ),
            }}
          />
        )}

        {/* ROUTE */}

        {rider && (
          <Polyline
            path={[
              rider,
              customerLocation,
            ]}
            options={{
              strokeColor:
                "#00b894",
              strokeOpacity: 1,
              strokeWeight: 5,
            }}
          />
        )}

      </GoogleMap>

      {/* ================= ORDER INFO ================= */}

      <div className="tracking-info mt-4">

        <div className="info-card">

          <h5>
            📦 Order Details
          </h5>

          <p>
            <strong>
              Order ID :
            </strong>{" "}
            #
            {order._id?.slice(
              -6
            )}
          </p>

          <p>
            <strong>
              Status :
            </strong>{" "}
            {order.orderStatus}
          </p>

          <p>
            <strong>
              Distance :
            </strong>{" "}
            {order.distance ||
              "--"}
          </p>

          <p>
            <strong>
              ETA :
            </strong>{" "}
            {order.etaText ||
              "--"}
          </p>

        </div>

        <div className="info-card">

          <h5>
            🏠 Delivery Address
          </h5>

          <p>
            {order
              ?.shippingAddress
              ?.address}
            ,
            {" "}
            {
              order
                ?.shippingAddress
                ?.city
            }
            {" - "}
            {
              order
                ?.shippingAddress
                ?.pincode
            }
          </p>

        </div>

      </div>

      {/* ================= STYLES ================= */}

      <style>{`

        .tracking-header{
          display:flex;
          justify-content:space-between;
          align-items:center;
          flex-wrap:wrap;
          gap:15px;
          margin-bottom:20px;
        }

        .tracking-header h3{
          margin:0;
          font-weight:700;
          color:#222;
        }

        .eta-box{
          background:#00b894;
          color:#fff;
          padding:10px 18px;
          border-radius:30px;
          font-size:15px;
          font-weight:600;
        }

        .tracking-info{
          display:grid;
          grid-template-columns:repeat(auto-fit,minmax(300px,1fr));
          gap:20px;
          margin-top:25px;
        }

        .info-card{
          background:#fff;
          border-radius:18px;
          padding:20px;
          box-shadow:0 8px 25px rgba(0,0,0,.08);
        }

        .info-card h5{
          margin-bottom:15px;
          font-weight:700;
          color:#333;
        }

        .info-card p{
          margin:8px 0;
          color:#555;
          line-height:1.5;
        }

        @media(max-width:768px){

          .tracking-header{
            flex-direction:column;
            align-items:flex-start;
          }

          .tracking-info{
            grid-template-columns:1fr;
          }

        }

      `}</style>

    </div>

  );

};

export default OrderTracking;

